import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Puzzle, CheckCircle2, Clock, Plus } from "lucide-react";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { listPlugins, createPlugin } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Plugins catalog page (iter-40, iter-44 scroll/compact, iter-45 CRUD).
 *
 * Tapping any enabled plugin navigates to its detail page where the user
 * can manage their saved prompts (and the superadmin can edit the plugin
 * itself). Disabled plugins render greyed out and are not clickable.
 *
 * Superadmin: extra "+ New plugin" button at the top.
 */
export default function PluginsPage() {
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperadmin = user?.role === "superadmin";

    const load = async () => {
        setLoading(true);
        try {
            setPlugins(await listPlugins());
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't load plugins",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleOpen = (plugin) => {
        // iter-47: superadmin can always open (to re-enable a disabled plugin).
        if (plugin.status !== "active" && !isSuperadmin) return;
        navigate(`/plugins/${plugin.id}`);
    };

    return (
        <div
            className="h-[100dvh] w-full overflow-y-auto bg-[#0A1128] text-slate-100 flex flex-col"
            data-testid="plugins-page"
        >
            <TopNav />
            <main className="flex-1 px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto w-full">
                <header className="mb-4 sm:mb-6 flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#00A3FF] font-bold">
                            <Puzzle className="w-3.5 h-3.5" />
                            Plugins
                        </div>
                        <h1 className="mt-1 text-2xl sm:text-4xl font-black tracking-tight text-white">
                            Trade plugins
                        </h1>
                        <p className="mt-2 max-w-2xl text-[13px] sm:text-sm text-slate-400 leading-relaxed">
                            Tap any plugin to manage its saved prompts. Active
                            plugins turn green in the floating bar on the
                            Workspace and feed prompts into Analyze & Estimate.
                        </p>
                    </div>
                    {isSuperadmin && (
                        <Button
                            onClick={() => setCreateOpen(true)}
                            data-testid="plugins-create-btn"
                            className="shrink-0 bg-emerald-500 hover:bg-emerald-600 text-white"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            New plugin
                        </Button>
                    )}
                </header>

                {loading ? (
                    <div
                        className="text-sm text-slate-500"
                        data-testid="plugins-loading"
                    >
                        Loading catalog…
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 pb-24">
                        {plugins.map((p) => (
                            <PluginCard
                                key={p.id}
                                plugin={p}
                                onActivate={handleOpen}
                                canOpenDisabled={isSuperadmin}
                            />
                        ))}
                    </div>
                )}
            </main>

            {isSuperadmin && (
                <CreatePluginDialog
                    open={createOpen}
                    onOpenChange={setCreateOpen}
                    onCreated={load}
                />
            )}
        </div>
    );
}

function PluginCard({ plugin, onActivate, canOpenDisabled = false }) {
    const isActive = plugin.status === "active";
    const clickable = isActive || canOpenDisabled;
    return (
        <button
            type="button"
            onClick={() => onActivate(plugin)}
            disabled={!clickable}
            data-testid={`plugin-card-${plugin.id}`}
            data-status={plugin.status}
            className={`group relative overflow-hidden rounded-lg border text-left transition-colors flex flex-col ${
                isActive
                    ? "border-[#2A3655] bg-[#0F1B3A] hover:border-[#00A3FF]/60 active:border-[#00A3FF] cursor-pointer"
                    : clickable
                        ? "border-amber-500/40 bg-[#0A1128] opacity-70 grayscale hover:opacity-90 cursor-pointer"
                        : "border-[#1C253C] bg-[#0A1128] opacity-40 grayscale cursor-not-allowed"
            }`}
        >
            <div className="relative bg-black/30 overflow-hidden aspect-square w-full">
                {plugin.icon_type === "video" && plugin.icon ? (
                    <video
                        src={plugin.icon}
                        poster={plugin.poster || undefined}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                        data-testid={`plugin-video-${plugin.id}`}
                    />
                ) : plugin.icon ? (
                    <img
                        src={plugin.icon}
                        alt={plugin.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div
                        className="w-full h-full flex items-center justify-center font-black text-3xl tracking-tight"
                        style={{
                            color: "#0a1128",
                            backgroundColor: plugin.accent || "#334155",
                        }}
                    >
                        {(plugin.name || "?")[0]}
                    </div>
                )}
                {!isActive && (
                    <div className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-slate-900/80 border border-slate-700 px-1.5 py-0.5 text-[8px] uppercase tracking-wider font-bold text-amber-300">
                        <Clock className="w-2 h-2" />
                        Off
                    </div>
                )}
                {isActive && (
                    <div className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/50 px-1.5 py-0.5 text-[8px] uppercase tracking-wider font-bold text-emerald-300">
                        <CheckCircle2 className="w-2 h-2" />
                        Active
                    </div>
                )}
            </div>
            <div className="p-2 sm:p-2.5">
                <h3
                    className="text-[13px] sm:text-sm font-bold tracking-tight text-white leading-tight"
                    style={isActive ? { color: plugin.accent || "#ffffff" } : {}}
                >
                    {plugin.name}
                </h3>
                <p className="mt-0.5 text-[10px] sm:text-[11px] text-slate-400 leading-snug line-clamp-2">
                    {plugin.description}
                </p>
            </div>
        </button>
    );
}

function CreatePluginDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        description: "",
        accent: "#00A3FF",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open)
            setForm({ name: "", description: "", accent: "#00A3FF" });
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Plugin name is required.");
            return;
        }
        setSaving(true);
        try {
            const plugin = await createPlugin({
                name: form.name.trim(),
                description: form.description.trim(),
                accent: form.accent,
            });
            toast.success("Plugin created");
            onOpenChange(false);
            onCreated();
            // Jump straight into its detail page so the user can upload
            // an icon + add their first prompt.
            window.location.href = `/plugins/${plugin.id}`;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0F1B3A] border-[#2A3655] text-slate-100 sm:max-w-md"
                data-testid="plugins-create-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">
                        New plugin
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                        Give it a name and accent — you can upload an icon
                        and add prompts on the next screen.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            Name
                        </label>
                        <Input
                            autoFocus
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder="e.g. Plumbing"
                            data-testid="plugins-create-name"
                            className="mt-1 bg-[#0A1128] border-[#2A3655] text-white"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            Description
                        </label>
                        <Textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    description: e.target.value,
                                })
                            }
                            placeholder="Short description of the trade scope"
                            data-testid="plugins-create-description"
                            className="mt-1 bg-[#0A1128] border-[#2A3655] text-white text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            Accent
                        </label>
                        <input
                            type="color"
                            value={form.accent}
                            onChange={(e) =>
                                setForm({ ...form, accent: e.target.value })
                            }
                            data-testid="plugins-create-accent"
                            className="w-10 h-8 rounded border border-[#2A3655] bg-[#0A1128] cursor-pointer"
                        />
                        <span className="text-[11px] text-slate-500 font-mono">
                            {form.accent}
                        </span>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="plugins-create-submit"
                            className="bg-emerald-500 hover:bg-emerald-600"
                        >
                            Create
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
